import type { ComponentPropsWithRef } from "react";
import { forwardRef, useMemo } from "react";
import { omit } from "lodash";
import { FieldElement } from "@/react/components/FormRenderer/FormContent/ElementTypes/FieldElement";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { FieldElement as FieldElementType } from "@/react/types/Element";
import { useBlocksContext } from "../../contexts/BlocksContext";

interface NumberBlockMeta {
  elementableId: number;
  elementId: number;
  fieldIndex: number;
  position: number;
}

export interface FormNumberBlockProps extends ComponentPropsWithRef<"div"> {
  blockInstanceMeta?: NumberBlockMeta;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export const FormNumberBlock = forwardRef<HTMLDivElement, FormNumberBlockProps>(
  (
    {
      label = "Number",
      placeholder = "",
      required = false,
      className,
      blockInstanceMeta = {},
      ...rest
    },
    ref,
  ) => {
    const elementProps = omit(rest, ["blockPath"]);
    const { useFormRenderingContext } = useBlocksContext();
    const context = useFormRenderingContext();
    const fallbackIndex = useMemo(() => Date.now(), []);

    const element: FieldElementType = {
      id: blockInstanceMeta.elementId ?? 0,
      elementable_id: blockInstanceMeta.elementableId ?? 0,
      position: blockInstanceMeta.position ?? 0,
      type: "field",
      attributes: {
        field_type: "number",
        label,
        placeholder,
        required,
        hidden: false,
      },
    };

    const errors =
      "backendErrors" in context ? (context.backendErrors ?? {}) : {};

    return (
      <div
        ref={ref}
        className={classNames("w-full", className)}
        {...elementProps}
      >
        <FieldElement
          element={element}
          fieldIndex={blockInstanceMeta.fieldIndex ?? fallbackIndex}
          errors={errors}
        />
      </div>
    );
  },
);

FormNumberBlock.displayName = "FormNumberBlock";
